/* eslint-disable complexity */
import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { UILoader } from '@futrli/ui-components';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';
import { workspaceByIdSelector } from '@futrli/shared-services';

import { ASSETS_CATEGORIES, StoreState, VIEW_TYPES } from '../../../../core/types';
import { CATEGORY_NAME_INDEXES, REPORT_SECTION } from '../../../../core/constants';
import { ForecastUpdating } from '../../components/forecast-updating';
import { ProfitLossPredictionsPageWrapper } from '../profit-loss-predictions-page-wrapper';
import { getConsolidatedGridInfo } from '../util-fns/consolidation-util-fns';
import { useConsolidationDataContext } from '../../../../predict-management/context/consolidation-data-context';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { UserGridSettingsContext } from '../../reports/user-grid-settings/user-grid-settings-context';

export const ConsolidationAssets = () => {
  const intl = useIntl();
  const { workspaceId: tempWorkspaceId } = useParams();
  const workspaceId = tempWorkspaceId || '';

  const {
    category,
    consolidatedForecastReport,
    forecast,
    isLoading,
    periodType,
    totalCashPredictRow,
    viewType
  } = usePredictionsInfoContext();

  const workspace = useSelector((state: StoreState) => workspaceByIdSelector(state.normalised, workspaceId));

  const { consolidation } = useConsolidationDataContext();

  const { nodeStaticIds } = useLocalisedConstantsContext();

  const { expandedRows, rowHeights, getShowZeroValueAccounts } = useContext(UserGridSettingsContext);

  const categoryName =
    category === ASSETS_CATEGORIES.NON_CURRENT_ASSETS
      ? CATEGORY_NAME_INDEXES.non_current_assets
      : category === ASSETS_CATEGORIES.CURRENT_ASSETS
      ? CATEGORY_NAME_INDEXES.current_assets
      : CATEGORY_NAME_INDEXES.fixed_assets;

  const viewOptions = useMemo(() => {
    return [
      {
        name: intl.formatMessage({
          id: 'predict.accruals_bs_view',
          defaultMessage: 'Accruals (BS) view'
        }),
        value: VIEW_TYPES.ACCRUALS_BS
      },
      {
        name: intl.formatMessage({
          id: 'predict.cash_flow_view',
          defaultMessage: 'Cash flow view'
        }),
        value: VIEW_TYPES.CASH_FLOW
      }
    ];
  }, [intl]);

  const categoryOptions = useMemo(() => {
    return [
      {
        name: intl.formatMessage({
          id: 'financial_fields.category.current_assets',
          defaultMessage: 'Current Assets'
        }),
        value: ASSETS_CATEGORIES.CURRENT_ASSETS
      },
      {
        name: intl.formatMessage({
          id: 'financial_fields.category.fixed_assets',
          defaultMessage: 'Fixed Assets'
        }),
        value: ASSETS_CATEGORIES.FIXED_ASSETS
      },
      {
        name: intl.formatMessage({
          id: 'predict.non_current_assets',
          defaultMessage: 'Non-current Assets'
        }),
        value: ASSETS_CATEGORIES.NON_CURRENT_ASSETS
      }
    ];
  }, [intl]);

  const { dataGridRows, totalRow } = useMemo(() => {
    return getConsolidatedGridInfo(
      intl,
      nodeStaticIds,
      viewType === VIEW_TYPES.CASH_FLOW ? REPORT_SECTION.CashFlow : REPORT_SECTION.BalanceSheet,
      categoryName,
      workspace,
      expandedRows,
      periodType,
      consolidatedForecastReport,
      consolidation,
      getShowZeroValueAccounts(forecast?.id || '')
    );
  }, [
    intl,
    nodeStaticIds,
    viewType,
    categoryName,
    workspace,
    expandedRows,
    periodType,
    consolidatedForecastReport,
    consolidation,
    getShowZeroValueAccounts,
    forecast?.id
  ]);

  if (!consolidatedForecastReport && isLoading) {
    return <UILoader isFull />;
  }

  return (
    <>
      <ProfitLossPredictionsPageWrapper
        reportColumns={
          viewType === VIEW_TYPES.CASH_FLOW
            ? consolidatedForecastReport?.data.cash_flow_columns
            : consolidatedForecastReport?.data.columns
        }
        rowHeights={rowHeights}
        viewOptions={viewOptions}
        accountRows={dataGridRows}
        totalRow={totalRow}
        category={categoryName}
        categoryOptions={categoryOptions}
        totalCashPredictRow={totalCashPredictRow}
      />
      <ForecastUpdating show={!!isLoading} />
    </>
  );
};
